import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { X } from 'lucide-react';
import { Capacitor } from '@capacitor/core';
import { Keyboard, type KeyboardInfo } from '@capacitor/keyboard';
import './ProPageModal.css';

export interface ProPageModalProps {
  /** Controls visibility. Pair with `onDismiss` to close. */
  isOpen: boolean;
  /** Called when the sheet dismisses via any path (close button, backdrop, Escape). */
  onDismiss: () => void;
  /** Title rendered in the sheet header. */
  title: string;
  /**
   * Optional trailing header action (e.g. "Done", "Apply"). If omitted, only
   * the close button is shown and a spacer keeps the title visually centered.
   */
  trailingAction?: {
    label: string;
    onClick: () => void;
    disabled?: boolean;
  };
  /** Sheet body — rendered in a scrollable container capped at ~88vh. */
  children: React.ReactNode;
}

/**
 * Pro mobile bottom-sheet primitive.
 *
 * Plain React fixed overlay, intentionally NOT an `IonModal`. Ionic's
 * overlays rely on `ion-app` being a standard containing block, but this
 * app's React Router scaffolding forces `ion-app` to `position: relative;
 * contain: none` so pages scroll normally (see index.css:24-32). That
 * breaks every Ionic overlay surface (IonModal full-screen, IonModal with
 * breakpoints, IonActionSheet, IonAlert). Every sheet in the Pro app goes
 * through this primitive instead.
 *
 * Portaled into `ion-app` (NOT `document.body`) so the sheet escapes
 * IonPage's stacking context (`contain: layout size style` traps fixed-
 * positioned descendants and confines their z-index) while staying inside
 * Ionic's CSS variable scope. The app's `index.css` sets `ion-app` to
 * `position: relative; contain: none` so it doesn't create its own
 * stacking-context trap — we get z-index isolation without losing theming.
 *
 * The portal root carries the `pro-ionic` class for Pro's dark-mode text
 * contrast tokens (e.g. `.dark .pro-ionic .text-text-dark-tertiary`).
 * Intentionally NOT `citizen-ionic`: that class also paints an opaque
 * `background: var(--ion-background-color)` which would cover the dimmed
 * page behind the sheet with a solid color. Instead, the `[class*="table"]`
 * un-hide override that `citizen-ionic` provides is replicated directly
 * on `.pro-sheet` in ProPageModal.css — same effect, no bg side effect.
 *
 * Shape: backdrop dims the page, a rounded-top panel slides up from the
 * bottom, caps at 88vh so tall content scrolls internally. Locks body
 * scroll while open and dismisses on Escape or backdrop tap. Consumers
 * pass children only; the primitive renders the header + scrollable body.
 */
const ProPageModal: React.FC<ProPageModalProps> = ({
  isOpen,
  onDismiss,
  title,
  trailingAction,
  children,
}) => {
  const [keyboardHeight, setKeyboardHeight] = useState(0);

  useEffect(() => {
    if (!isOpen) return;
    const previous = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = previous;
    };
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onDismiss();
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [isOpen, onDismiss]);

  // Keep the panel above the on-screen keyboard. iOS WKWebView keeps the
  // visual viewport at full height even with `Keyboard.resize: 'body'`
  // (set in capacitor.config.ts), so `position: fixed; bottom: 0` would
  // sit behind the keyboard — we lift it manually by keyboardHeight.
  // Android's WebView does resize, so the lift isn't needed, but the
  // panel's `padding-bottom: env(safe-area-inset-bottom)` becomes a
  // visible empty strip while the keyboard occupies that space — zero
  // it out on both platforms while the keyboard is up.
  useEffect(() => {
    if (!isOpen) {
      setKeyboardHeight(0);
      return;
    }
    if (!Capacitor.isNativePlatform()) return;

    let cancelled = false;
    const handlePromises = [
      Keyboard.addListener('keyboardWillShow', (info: KeyboardInfo) => {
        if (!cancelled) setKeyboardHeight(info.keyboardHeight);
      }),
      Keyboard.addListener('keyboardWillHide', () => {
        if (!cancelled) setKeyboardHeight(0);
      }),
    ];

    return () => {
      cancelled = true;
      handlePromises.forEach((p) => {
        p.then((h) => h.remove()).catch(() => undefined);
      });
    };
  }, [isOpen]);

  if (!isOpen) return null;

  const panelStyle: React.CSSProperties = {};
  if (keyboardHeight > 0) {
    panelStyle.paddingBottom = 0;
    if (Capacitor.getPlatform() === 'ios') {
      panelStyle.bottom = keyboardHeight;
    }
  }

  const portalTarget = document.querySelector('ion-app') ?? document.body;

  return createPortal(
    <div
      className="pro-sheet pro-ionic"
      role="dialog"
      aria-modal="true"
      aria-label={title}
    >
      <div className="pro-sheet__backdrop" onClick={onDismiss} aria-hidden />
      <div className="pro-sheet__panel" style={panelStyle}>
        <div className="pro-sheet__handle" aria-hidden />
        <div className="pro-sheet__header">
          <button
            type="button"
            onClick={onDismiss}
            aria-label="Close"
            className="pro-sheet__close"
          >
            <X className="w-5 h-5" />
          </button>
          <h2 className="pro-sheet__title">{title}</h2>
          {trailingAction ? (
            <button
              type="button"
              onClick={trailingAction.onClick}
              disabled={trailingAction.disabled}
              className="pro-sheet__action"
            >
              {trailingAction.label}
            </button>
          ) : (
            <span aria-hidden className="pro-sheet__action-spacer" />
          )}
        </div>
        <div className="pro-sheet__body">{children}</div>
      </div>
    </div>,
    portalTarget,
  );
};

export default ProPageModal;
